import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchChatDetailsAPI,
  fetchChatHistoryListAPI,
} from "../../services/apiServices";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { usePropertyStore } from "../../redux/features/propertyDetailSlice";
import { useRedirectStore } from "../../redux/features/userRedirectSlice";
import { useAuthStore } from "../../redux/features/authSlice";
import { useTranslation } from "react-i18next";

const HistorySection = () => {
  const navigate = useNavigate();
  const { loginInfo, removeLoginInfo } = useAuthStore();
  const { userRedirectFlag, updateUserRedirectState } = useRedirectStore();
  const [chatListTitle, setChatListTitle] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDateIndex, setOpenDateIndex] = useState();
  const { propertyInfo, updatePropertyInfo } = usePropertyStore();
  const { t, i18n } = useTranslation();

  // Helper to get language-prefixed path
  const getLangPath = (path) => {
    if (i18n.language === 'en') {
      return path.startsWith('/en') ? path : '/en' + (path.startsWith('/') ? path : '/' + path);
    }
    return path;
  };

  const fetchChatHistoryList = async () => {
    try {
      setLoading(true);
      const response = await fetchChatHistoryListAPI();
      if (response?.data?.statusCode == 200) {
        setChatListTitle(response?.data?.data);
      } else if (response?.data?.statusCode == 401) {
        toast.warning(
          response?.data?.message || t("signin_session_expired", "Your session has been expired."),
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
        updateUserRedirectState();
        removeLoginInfo();
        navigate(getLangPath("/"));
      } else {
        console.warn(
          "error fetching chat list with title in API gives 401",
          response
        );
      }
    } catch (error) {
      console.warn("error============", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDate = (index) => {
    setOpenDateIndex(openDateIndex === index ? null : index);
  };

  const onClickChat = async (id) => {
    try {
      setLoading(true);
      const response = await fetchChatDetailsAPI({ chatHistoryId: id });
      if (response?.data?.statusCode == 200) {
        const { chatHistory } = response?.data?.data;
        if (chatHistory?.length > 0) {
          if (chatHistory?.length >= 1) {
            updatePropertyInfo({
              ...propertyInfo,
              calculatedSize: {
                size: chatHistory[0]?.filters
                  ? chatHistory[0]?.filters.officeCalculatedSize
                  : [0, 0],
                sizeCount: chatHistory[0]?.locationCount,
              },
              location: {
                latLng: chatHistory[0]?.filters
                  ? chatHistory[0]?.filters?.propertyLocation?.latLng
                  : "",
                address: chatHistory[0]?.filters
                  ? chatHistory[0]?.filters?.propertyLocation?.address
                  : "",
                locationCount: chatHistory[0]?.locationCount,
              },
              movePlan: {
                answer: chatHistory[0]?.filters?.movePlan
                  ? chatHistory[0]?.filters?.movePlan
                  : "",
              },
              count: chatHistory[0]?.count,
            });
          }
          if (chatHistory?.length >= 2) {
            updatePropertyInfo({
              ...propertyInfo,
              budgetPricing: {
                answer: chatHistory[1]?.filters?.monthlyRentalBudget
                  ? chatHistory[1]?.filters?.monthlyRentalBudget
                  : "",
              },
              count: chatHistory[1]?.count,
            });
          }
          //here
          if (chatHistory?.length >= 3) {
            updatePropertyInfo({
              ...propertyInfo,
              preferredLocation: {
                answer: chatHistory[2]?.filters?.preferredLocation
                  ? chatHistory[2]?.filters?.preferredLocation
                  : "",
              },
              count: chatHistory[2]?.count,
            });
          }

          if (chatHistory?.length >= 4) {
            updatePropertyInfo({
              ...propertyInfo,
              officeType: {
                answer: chatHistory[3]?.filters?.officeType
                  ? chatHistory[3]?.filters?.officeType
                  : "",
              },
              count: chatHistory[3]?.count,
            });
          }

          if (chatHistory?.length >= 5) {
            updatePropertyInfo({
              ...propertyInfo,
              leaseDuration: {
                answer: chatHistory[4]?.filters?.leaseTermsDuration
                  ? chatHistory[4]?.filters?.leaseTermsDuration
                  : "",
              },
              count: chatHistory[4]?.count,
            });
          }

          if (chatHistory?.length >= 6) {
            updatePropertyInfo({
              ...propertyInfo,
              officeSpaces: {
                answer: chatHistory[5]?.filters?.officeSpaces
                  ? chatHistory[5]?.filters?.officeSpaces
                  : "",
              },
              count: chatHistory[5]?.count,
            });
          }

          if (chatHistory?.length >= 7) {
            updatePropertyInfo({
              ...propertyInfo,
              amenities: {
                answer: chatHistory[6]?.filters?.amenities
                  ? chatHistory[6]?.filters?.amenities
                  : "",
              },
              count: chatHistory[6]?.count,
            });
          }

          if (chatHistory?.length === 8) {
            updatePropertyInfo({
              ...propertyInfo,
              parking: {
                answer: chatHistory[7]?.filters?.parking
                  ? chatHistory[7]?.filters?.parking
                  : "",
              },
              count: chatHistory[7]?.count,
            });
          }
        }
        // updateUserRedirectState();
        // navigate("/ai-chat");
        navigate(getLangPath(`/chat-history?id=${id}`));
      } else {
        console.warn(
          "error fetching chat list with title in API gives 401",
          response
        );
      }
    } catch (error) {
      console.warn("error============", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchChatHistoryList();
  }, [userRedirectFlag]);
  return (
    <div className="bg-secondary h-full w-[300px] pt-10 pl-10 pr-5 min-h-[calc(100vh-4rem)] overflow-y-scroll">
      <h2 className="text-2xl font-bold text-white">{t('view_history', 'Chat History')}</h2>

      <hr className="border-white w-full my-4" />

      <div>
        <ul className="text-white font-medium text-base">
          {chatListTitle && chatListTitle?.length > 0
            ? chatListTitle?.map((item, index) => {
                return (
                  <li key={index} className="mb-2">
                    {/* Date Toggle */}
                    <div
                      onClick={() => toggleDate(index)}
                      className="p-2 cursor-pointer truncate hover:opacity-50 bg-gray-1000 rounded-lg flex gap-2 justify-between items-center"
                    >
                      <span className="text-lg">{item?.date}</span>
                      {openDateIndex === index ? (
                        <ChevronUp
                          size={20}
                          className="transition-transform duration-300"
                        />
                      ) : (
                        <ChevronDown
                          size={20}
                          className="transition-transform duration-300"
                        />
                      )}
                    </div>
                    {/* Chat List (Toggled) */}
                    {openDateIndex === index &&
                      item?.properties?.length > 0 && (
                        <ul className="mt-2 ml-4">
                          {item.properties.map((chat, chatIndex) => (
                            <li
                              key={chatIndex}
                              className="p-2 cursor-pointer truncate hover:opacity-50"
                              onClick={() => onClickChat(chat?._id)}
                            >
                              {chat?.title}
                            </li>
                          ))}
                        </ul>
                      )}
                  </li>
                );
              })
            : ""}
        </ul>
      </div>
    </div>
  );
};

export default HistorySection;
